import type { MembershipPlan, Price } from '$lib/types/types';
import type { SupabaseClient } from '@supabase/supabase-js';

export async function getMembershipPlans(
	balancedSupabase: SupabaseClient
): Promise<MembershipPlan[]> {
	const { data: memberships, error } = await balancedSupabase
		.from('memberships')
		.select(
			`
        id,
        code,
        name,
        description,
        recommended,
        order,
        stripe_product_id,
        memberships_to_features(
          membership_features(
            id,
            code,
            name,
            description,
            order,
            policy
          )
        ),
        stripe_products(
          stripe_prices(
            id,
            unit_amount,
            currency,
            recurring_interval,
            recurring_interval_count
          )
        )
      `
		)
		.eq('stripe_products.stripe_prices.active', true)
		.order('order', { ascending: true, nullsLast: true });

	if (error) {
		console.error('Error fetching memberships:', error);
		return [];
	}

	const processedMemberships: MembershipPlan[] = memberships.map((membership) => {
		const features = membership.memberships_to_features
			.map((mtf) => mtf.membership_features)
			.flat()
			.sort((a, b) => {
				if (a.order === null && b.order === null) return 0;
				if (a.order === null) return 1;
				if (b.order === null) return -1;
				return a.order - b.order;
			});

		const prices = membership.stripe_products?.stripe_prices ?? [];
		const monthlyPrice = prices.find(
			(price: Price) => price.recurring_interval === 'month' && price.recurring_interval_count === 1
		);
		const yearlyPrice = prices.find(
			(price: Price) => price.recurring_interval === 'year' && price.recurring_interval_count === 1
		);

		return {
			id: membership.id,
			code: membership.code,
			name: membership.name,
			description: membership.description,
			recommended: membership.recommended,
			stripe_product_id: membership.stripe_product_id,
			features: features.map((f) => ({
				id: f.id,
				code: f.code,
				name: f.name,
				description: f.description,
				order: f.order,
				policy: f.policy
			})),
			prices: {
				monthly: monthlyPrice
					? {
							id: monthlyPrice.id,
							unit_amount: monthlyPrice.unit_amount,
							currency: monthlyPrice.currency
						}
					: null,
				yearly: yearlyPrice
					? {
							id: yearlyPrice.id,
							unit_amount: yearlyPrice.unit_amount,
							currency: yearlyPrice.currency
						}
					: null
			}
		};
	});

	return processedMemberships;
}

export async function getMembershipFeatures(balancedSupabase: SupabaseClient) {
	const { data: features, error } = await balancedSupabase
		.from('membership_features')
		.select('id, name, description')
		.order('order', { ascending: true })
		.order('name', { ascending: true });
	if (error) {
		console.error(error);
		return [];
	}
	return features;
}
